import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { goPro } from '../lib/checkout';
import { triggerExitIntent } from '../lib/exitIntent';
import { ProtectedContent } from '../components/ContentProtection';
import { MobileSectionPicker } from '../components/courses/MobileSectionPicker';
import { ArrowLeft, CheckCircle, ChevronRight, ChevronLeft, BookOpen, FileText, ExternalLink, Download } from 'lucide-react';

function extractLoomId(value) {
  if (!value) return '';
  value = value.trim();
  if (value.includes('loom.com')) {
    const match = value.match(/loom\.com\/(?:share|embed)\/([a-f0-9]+)/);
    if (match) return match[1];
  }
  return value;
}

export default function CourseViewer() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    // No client-side plan gate here — the backend is the source of truth.
    // Free courses are open to everyone; pro courses return 403 for free
    // users, which we turn into the Pro upgrade window.
    api.get(`/api/courses/${courseSlug}`)
      .then(r => setCourse(r.data))
      .catch(err => {
        navigate('/courses');
        if (err.response?.status === 403) goPro();
      })
      .finally(() => setLoading(false));
  }, [courseSlug, navigate]);

  // Active watch-time heartbeat (30s ticks while the tab is visible) —
  // powers accurate course-usage stats and refund eligibility.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        api.post('/api/courses/heartbeat', { course_slug: courseSlug, seconds: 30 }).catch(() => {});
      }
    }, 30000);
    return () => clearInterval(id);
  }, [courseSlug]);

  const markComplete = async (sectionId) => {
    await api.post(`/api/courses/${courseSlug}/complete-section`, { section_id: sectionId });
    const updated = { ...course };
    updated.sections = updated.sections.map(s => s.id === sectionId ? { ...s, is_completed: true } : s);
    setCourse(updated);
    const wasDone = (course.sections || []).every(s => s.is_completed);
    if (!wasDone && updated.sections.length && updated.sections.every(s => s.is_completed)) triggerExitIntent('course_done', 600);
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div></DashboardLayout>;
  if (!course) return <DashboardLayout><p>Course not found</p></DashboardLayout>;

  const sections = course.sections || [];
  const currentSection = sections[activeSection];
  const completedCount = sections.filter(s => s.is_completed).length;
  const progressPct = sections.length > 0 ? Math.round(completedCount / sections.length * 100) : 0;

  const pickSection = (i) => { setActiveSection(i); if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <DashboardLayout>
      <ProtectedContent watermark={user?.email || ''}>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6" data-testid="course-viewer">
        <MobileSectionPicker course={course} sections={sections} activeSection={activeSection} onPick={pickSection} completedCount={completedCount} progressPct={progressPct} />
        {/* Sidebar */}
        <div className="hidden lg:block w-72 shrink-0">
          <button onClick={() => navigate('/courses')} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 mb-4">
            <ArrowLeft size={14} />All Courses
          </button>
          <div className="bg-white rounded-xl border border-neutral-200 p-4">
            <h2 className="font-semibold text-neutral-900 text-sm mb-1">{course.title}</h2>
            <div className="flex items-center gap-2 text-xs text-neutral-500 mb-3">
              <span>{completedCount}/{sections.length} complete</span>
              <span>({progressPct}%)</span>
            </div>
            <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-primary rounded-full" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="space-y-1">
              {sections.map((s, i) => (
                <button key={s.id} onClick={() => setActiveSection(i)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition ${
                    i === activeSection ? 'bg-primary/10 text-primary font-medium' : 'text-neutral-600 hover:bg-neutral-50'
                  }`} data-testid={`section-nav-${i}`}>
                  {s.is_completed ? (
                    <CheckCircle size={16} className="text-green-500 shrink-0" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-neutral-300 shrink-0" />
                  )}
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-3xl min-w-0">
          {currentSection && (
            <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 text-xs text-neutral-400 mb-2">
                <BookOpen size={12} />Section {activeSection + 1} of {sections.length}
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-neutral-900 mb-4 sm:mb-6" data-testid="course-section-title">{currentSection.title}</h1>

              {/* Loom Video — protected embed (no share/copy controls) */}
              {currentSection.loom_video_id && (
                <div
                  className="mb-6 rounded-lg overflow-hidden aspect-video relative"
                  data-testid="course-loom-video"
                  onContextMenu={e => e.preventDefault()}
                  style={{ userSelect: 'none' }}
                >
                  <iframe
                    src={`https://www.loom.com/embed/${extractLoomId(currentSection.loom_video_id)}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`}
                    title={currentSection.title}
                    frameBorder="0"
                    allowFullScreen
                    className="w-full h-full"
                    allow="autoplay; fullscreen"
                    referrerPolicy="no-referrer"
                  />
                  {/* Overlay to block Loom's top control bar (copy link, open in new tab etc.) */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '54px', zIndex: 10, cursor: 'default' }} />
                </div>
              )}

              {/* Markdown content */}
              <div className="prose prose-sm max-w-none text-neutral-800 leading-relaxed" data-testid="section-content">
                {(currentSection.content_markdown || '').split('\n').map((line, i) => {
                  if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-neutral-900 mt-6 mb-3">{line.replace('## ', '')}</h2>;
                  if (line.startsWith('- ')) return <li key={i} className="ml-4 text-sm text-neutral-700 mb-1">{line.replace('- ', '')}</li>;
                  if (line.trim() === '') return <br key={i} />;
                  return <p key={i} className="text-sm text-neutral-700 mb-2">{line}</p>;
                })}
              </div>

              {/* Resources (PDFs, Links) */}
              {currentSection.resources && currentSection.resources.length > 0 && (
                <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid="section-resources">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText size={16} className="text-blue-600" />
                    Resources
                  </h3>
                  <div className="space-y-2">
                    {currentSection.resources.map((r, i) => (
                      <a
                        key={i}
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition group"
                        data-testid={`resource-${i}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          r.type === 'pdf' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {r.type === 'pdf' ? <Download size={16} /> : <ExternalLink size={16} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{r.title || r.url}</p>
                          <p className="text-xs text-slate-400 truncate">{r.type === 'pdf' ? 'PDF Document' : r.url}</p>
                        </div>
                        <ExternalLink size={14} className="text-slate-300 group-hover:text-blue-500 transition shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-neutral-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="course-section-actions">
                <div>
                  {!currentSection.is_completed ? (
                    <button onClick={() => markComplete(currentSection.id)}
                      className="w-full sm:w-auto justify-center px-4 py-2.5 sm:py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                      data-testid="mark-complete-btn">
                      <CheckCircle size={16} />Mark as Complete
                    </button>
                  ) : (
                    <span className="flex items-center gap-2 text-green-600 text-sm font-medium"><CheckCircle size={16} />Completed</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {activeSection > 0 && (
                    <button onClick={() => pickSection(activeSection - 1)} className="flex-1 sm:flex-none justify-center px-4 py-2.5 sm:py-2 border border-neutral-300 rounded-lg text-sm hover:bg-neutral-50 transition flex items-center gap-1" data-testid="course-prev-btn"><ChevronLeft size={14} className="sm:hidden" />Previous</button>
                  )}
                  {activeSection < sections.length - 1 && (
                    <button onClick={() => pickSection(activeSection + 1)} className="flex-1 sm:flex-none justify-center px-4 py-2.5 sm:py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition flex items-center gap-1" data-testid="course-next-btn">
                      Next <ChevronRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </ProtectedContent>
    </DashboardLayout>
  );
}
